import React, { useMemo, useState } from 'react';
import { Alert, Button, Empty, Input, Space, Spin, Table, Tag, Tooltip, Typography, message } from 'antd';
import ReasonModal from './ReasonModal';
import EditQuestionModal from './EditQuestionModal';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { ApiError, userMessageForError } from '../../utils/apiError';
import { classifyQuestionRows, clauseFieldFor, isStaleEditConflict, staleEditMessage } from '../../utils/questionBankCuratorModel';

const { Text } = Typography;

/**
 * Questions of the selected standard that a curator can act on:
 *   published (live in the current version, immutable): Revise / Retire - both draft the NEXT version
 *   draft (unpublished): Edit in place / Retire
 *   queued for retirement: shown, no action
 * Older superseded versions are not listed (projects pinned to them keep reading them untouched).
 * Commandment VII; PRD s2.2 (masters are platform_admin only).
 */
const QuestionsPanel = ({ standardId, preview, rev, onChanged }) => {
    const { request } = useApiRequest();
    const [search, setSearch] = useState('');
    const [q, setQ] = useState('');
    const [modal, setModal] = useState(null); // { kind: 'revise' | 'edit' | 'retire', row }

    const publishedId = preview?.publishedVersion?.id || null;
    const draftId = preview?.draftVersion?.id || null;
    const clauseField = clauseFieldFor(standardId);

    const rows = useResource(
        async () => (await request('GET', '/admin/question-bank/questions', { query: { standardId, q } })).data.questions || [],
        [request, standardId, q, rev],
    );
    const aiDrafts = useResource(
        async () => (await request('GET', '/admin/question-bank/ai-drafts', { query: { standardId } })).data.drafts || [],
        [request, standardId, rev],
    );
    const aiStateByQuestion = useMemo(() => new Map((aiDrafts.data || []).map((d) => [d.questionId, d.state])), [aiDrafts.data]);
    const visible = useMemo(() => classifyQuestionRows(rows.data, publishedId, draftId), [rows.data, publishedId, draftId]);

    const closeModal = () => setModal(null);
    const saved = (verb) => async (data) => {
        closeModal();
        if (data?.approvalReset) message.warning('Saved. Your approval was withdrawn - approve the question again under Review AI drafts.');
        else message.success(data?.message || verb);
        await onChanged();
    };
    const retire = async (reason) => {
        let data;
        try {
            ({ data } = await request('POST', `/admin/question-bank/questions/${modal.row.id}/retire`, { body: { reason } }));
        } catch (err) {
            if (!isStaleEditConflict(err)) throw err;
            await onChanged();
            throw new ApiError(staleEditMessage(), 409, err.code, err.details);
        }
        closeModal();
        message.success(data?.message || 'Question retired.');
        await onChanged();
    };

    const stateTag = (r) => {
        if (r.rowKind === 'published') return <Tag color="green">Published {preview?.publishedVersion?.label || ''}</Tag>;
        if (r.rowKind === 'draft') {
            const ai = aiStateByQuestion.get(r.id);
            return (
                <Space wrap>
                    <Tag color="orange">{r.revisesPublished ? 'Revision pending' : 'Draft'}</Tag>
                    {ai && <Tag color={ai === 'APPROVED' ? 'green' : 'orange'}>{ai === 'APPROVED' ? 'AI drafted, approved' : 'AI drafted - review'}</Tag>}
                </Space>
            );
        }
        return <Tag color="red">{r.revisesPublished ? 'Retirement queued' : 'Retired draft'}</Tag>;
    };

    const actions = (r) => {
        if (r.rowKind === 'published') {
            const why = 'This question already has a pending change in the next version.';
            return (
                <Space>
                    <Tooltip title={r.hasPendingChange ? why : ''}>
                        <Button size="small" disabled={r.hasPendingChange} onClick={() => setModal({ kind: 'revise', row: r })}>Revise</Button>
                    </Tooltip>
                    <Tooltip title={r.hasPendingChange ? why : ''}>
                        <Button size="small" danger disabled={r.hasPendingChange} onClick={() => setModal({ kind: 'retire', row: r })}>Retire</Button>
                    </Tooltip>
                </Space>
            );
        }
        if (r.rowKind === 'draft') {
            return (
                <Space>
                    <Button size="small" onClick={() => setModal({ kind: 'edit', row: r })}>Edit</Button>
                    <Button size="small" danger onClick={() => setModal({ kind: 'retire', row: r })}>Retire</Button>
                </Space>
            );
        }
        return null;
    };

    const columns = [
        { title: 'No.', dataIndex: 'questionNo', key: 'questionNo', width: 110 },
        { title: 'Question', dataIndex: 'question', key: 'question' },
        { title: 'Clause', key: 'clause', width: 140, render: (_, r) => (clauseField ? r[clauseField] || '-' : '-') },
        { title: 'Priority', dataIndex: 'priority', key: 'priority', width: 90, render: (p) => p || '-' },
        { title: 'State', key: 'state', width: 200, render: (_, r) => stateTag(r) },
        { title: '', key: 'actions', width: 170, render: (_, r) => actions(r) },
    ];

    const row = modal?.row;
    const isPublished = row?.rowKind === 'published';

    return (
        <div>
            {preview?.publishedVersion == null && (
                <Alert type="info" style={{ marginBottom: 12 }} message="This standard has no published question bank version yet. Drafts you approve will create its first version." />
            )}
            <div style={{ marginBottom: 12 }}>
                <Input.Search
                    aria-label="Search questions"
                    placeholder="Search question text or number"
                    style={{ width: 320 }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onSearch={(v) => setQ(String(v || '').trim())}
                />
            </div>
            {rows.status === 'loading' && !rows.data && <Spin />}
            {rows.status === 'error' && (
                <Alert type="error" message={userMessageForError(rows.error)} action={<Button size="small" onClick={rows.reload}>Retry</Button>} />
            )}
            {rows.data && visible.length === 0 && (
                <Empty description={q ? 'No questions match your search.' : 'No questions for this standard yet. Use Draft with AI to create the first drafts.'} />
            )}
            {rows.data && visible.length > 0 && (
                <Table size="small" rowKey={(r) => r.id} columns={columns} dataSource={visible} pagination={{ pageSize: 25, showSizeChanger: false }} />
            )}

            <EditQuestionModal
                open={modal?.kind === 'revise' || modal?.kind === 'edit'}
                mode={modal?.kind === 'revise' ? 'revise' : 'edit'}
                row={modal?.kind === 'retire' ? null : row}
                standardId={standardId}
                approvalWillReset={modal?.kind === 'edit' && aiStateByQuestion.get(row?.id) === 'APPROVED'}
                onStale={onChanged}
                onClose={closeModal}
                onSaved={modal?.kind === 'revise' ? saved('Revision drafted for the next version.') : saved('Saved.')}
            />
            <ReasonModal
                open={modal?.kind === 'retire'}
                title={`Retire question ${row?.questionNo || ''}`}
                intro={isPublished
                    ? 'The question stays live until you publish the next version; retiring it queues its removal from that version. Projects already pinned to the current version keep it; only projects that pin after the next version is published stop seeing it. Your reason is recorded in the audit log.'
                    : 'This draft was never published. It will be retired now and will not appear in any version. Your reason is recorded in the audit log.'}
                okText={isPublished ? 'Queue retirement' : 'Retire draft'}
                onSubmit={retire}
                onClose={closeModal}
            />
            {rows.data && <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>Showing the current published version and the pending next version.</Text>}
        </div>
    );
};

export default QuestionsPanel;
